import React, { useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
  Typography,
} from "antd";
import ReactQuill from "react-quill";

const { Option } = Select;
const { Text } = Typography;

const HOURS_PER_MONTH = 176;

const REGIONS = [
  "America",
  "Canada",
  "UK",
  "India",
  "UAE",
  "Germany",
  "Australia",
  "New Zealand",
  "Nigeria",
  "Israel",
  "France",
  "Singapore",
  "Netherlands",
  "Belgium",
  "Spain",
  "Philippines",
  "Italy",
  "Bulgaria",
  "Greece",
  "Bahrin",
  "Norway",
];

const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["clean"],
  ],
};

const QUILL_FORMATS = ["bold", "italic", "underline", "list", "bullet"];

export default function CreateRequirementModal({
  open,
  onCancel,
  onSubmit,
  users,
  currentUser,
  submitting,
}) {
  const [form] = Form.useForm();
  const [description, setDescription] = useState("");

  /* ── auto-calculate project value ↔ hourly rate ───────────────
     totalHours = durationMonths * 176
     projectValue = hourlyRate * totalHours
     hourlyRate   = projectValue / totalHours
     Whichever field the user is NOT typing in gets recalculated.
  ───────────────────────────────────────────────────────────── */
  const recalcFromHourlyRate = (hourlyRate) => {
    const duration = form.getFieldValue("duration");
    if (!duration || !hourlyRate) return;
    const totalHours = duration * HOURS_PER_MONTH;
    form.setFieldsValue({ projectValue: Math.round(hourlyRate * totalHours) });
  };

  const recalcFromProjectValue = (projectValue) => {
    const duration = form.getFieldValue("duration");
    if (!duration || !projectValue) return;
    const totalHours = duration * HOURS_PER_MONTH;
    form.setFieldsValue({
      hourlyRate: Math.round((projectValue / totalHours) * 100) / 100,
    });
  };

  // When duration changes, recalc using whichever value already exists
  const handleDurationChange = (duration) => {
    const hourlyRate = form.getFieldValue("hourlyRate");
    const projectValue = form.getFieldValue("projectValue");

    if (hourlyRate) {
      recalcFromHourlyRate(hourlyRate);
    } else if (projectValue) {
      recalcFromProjectValue(projectValue);
    }
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        onSubmit({ ...values, description });
        form.resetFields();
        setDescription("");
      })
      .catch(() => {});
  };

  const handleCancel = () => {
    form.resetFields();
    setDescription("");
    onCancel();
  };

  return (
    <Modal
      title={
        <span style={{ color: "#13487D", fontWeight: 700, fontSize: 17 }}>
          New Requirement
        </span>
      }
      open={open}
      onCancel={handleCancel}
      width={720}
      footer={[
        <Button key="cancel" onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleOk}
          loading={submitting}
          style={{ background: "#3699FF", borderColor: "#3699FF" }}
        >
          Create Requirement
        </Button>,
      ]}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ assignedTo: currentUser?._id }}
        style={{ marginTop: 8 }}
      >
        {/* Job Name */}
        <Form.Item
          name="jobName"
          label="Job Name"
          rules={[{ required: true, message: "Please enter the job name" }]}
        >
          <Input placeholder="e.g. Senior React Developer" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="region"
              label="Region"
              rules={[{ required: true, message: "Please select a region" }]}
            >
              <Select
                placeholder="Select region"
                showSearch
                optionFilterProp="children"
              >
                {REGIONS.map((r) => (
                  <Option key={r} value={r}>
                    {r}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="duration"
              label="Duration (Months)"
              rules={[
                { required: true, message: "Please enter duration in months" },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={1}
                max={60}
                placeholder="e.g. 6"
                addonAfter="mo"
                onChange={handleDurationChange}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="projectValue"
              label="Project Value (USD)"
              rules={[
                {
                  required: true,
                  message: "Please enter project value or hourly rate",
                },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                formatter={(v) =>
                  `$ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(v) => v.replace(/\$\s?|(,*)/g, "")}
                placeholder="Auto-calculated or enter manually"
                onChange={recalcFromProjectValue}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="hourlyRate" label="Hourly Rate (USD)">
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                formatter={(v) => (v ? `$ ${v}` : "")}
                parser={(v) => v.replace(/\$\s?/g, "")}
                placeholder="Auto-calculated or enter manually"
                onChange={recalcFromHourlyRate}
              />
            </Form.Item>
          </Col>
        </Row>

        <Text
          type="secondary"
          style={{
            fontSize: 12,
            display: "block",
            marginTop: -12,
            marginBottom: 16,
          }}
        >
          Enter either Project Value or Hourly Rate — the other is calculated
          automatically using {HOURS_PER_MONTH} hrs/month × duration.
        </Text>

        {/* Company + SPOC details — all optional */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="companyName" label="Company Name">
              <Input placeholder="e.g. Acme Corp" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="sourceLink" label="Source Link">
              <Input placeholder="e.g. Upwork job link" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="spocName" label="SPOC Name">
              <Input placeholder="e.g. John Smith" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="spocEmail"
              label="SPOC Email"
              rules={[{ type: "email", message: "Enter a valid email" }]}
            >
              <Input placeholder="e.g. john@acme.com" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="spocNumber" label="SPOC Number">
              <Input placeholder="e.g. +1 555 123 4567" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          {/* Created By — read only display */}
          <Col span={12}>
            <Form.Item label="Created By">
              <Input value={currentUser?.name || ""} disabled />
            </Form.Item>
          </Col>

          {/* Assigned To */}
          <Col span={12}>
            <Form.Item
              name="assignedTo"
              label="Assigned To"
              rules={[{ required: true, message: "Please assign to someone" }]}
            >
              <Select
                placeholder="Select person"
                showSearch
                optionFilterProp="children"
              >
                {currentUser?._id && (
                  <Option key={currentUser._id} value={currentUser._id}>
                    {currentUser.name} (me)
                  </Option>
                )}
                {users
                  .filter((u) => u._id !== currentUser?._id)
                  .map((u) => (
                    <Option key={u._id} value={u._id}>
                      {u.name}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Description */}
        <Form.Item
          label={
            <span>
              Description{" "}
              <span style={{ color: "#BAC7D6", fontWeight: 400, fontSize: 12 }}>
                — Optional
              </span>
            </span>
          }
        >
          <div className="modal-quill-wrapper">
            <ReactQuill
              theme="snow"
              value={description}
              onChange={setDescription}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Add context, skills required, client notes..."
            />
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}
